/**
 * @file Read-only preview of current page content (draft or published).
 */
import { Link, useSearchParams, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { PageStatusBadge } from "@/components/PageStatusBadge";
import { pageDetailHref } from "@/lib/pages-navigation";

interface PageDetail {
  id: string;
  title: string;
  slug: string;
  status: string;
  chapter: {
    id: string;
    title: string;
    slug: string;
    guide: { id: string; title: string; slug: string };
  };
  currentVersion: { title: string; body: string } | null;
}

export function PagePreviewPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id as string;
  const listChapterId = searchParams.get("chapterId");
  const [page, setPage] = useState<PageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<PageDetail>(`/api/v1/pages/${id}`)
      .then(setPage)
      .catch((e) => setError(String(e)));
  }, [id]);

  if (error) {
    return <p className="text-rose-600">{error}</p>;
  }
  if (!page) {
    return <div className="text-zinc-500">Loading preview…</div>;
  }

  const html = page.currentVersion?.body ?? "<p><em>No content yet.</em></p>";

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={pageDetailHref(page.id, listChapterId)} className="text-sm text-sky-600 hover:underline">
            ← Back to editor
          </Link>
          <h1 className="mt-2 text-3xl font-semibold text-zinc-900">{page.title}</h1>
          <p className="text-sm text-zinc-500">
            {page.chapter.guide.title} › {page.chapter.title} · /{page.slug} · preview
          </p>
        </div>
        <PageStatusBadge status={page.status} />
      </div>

      <article
        className="preview-content rounded-xl border border-zinc-200 bg-white p-8 shadow-sm [&_a]:text-sky-700 [&_a]:underline [&_iframe]:aspect-video [&_iframe]:w-full [&_img]:max-w-full [&_video]:max-w-full [&_ul]:list-disc [&_ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
